/**
 * Inspection Service
 *
 * Handles inspection-related API calls using the centralized API client.
 */

import apiClient from './apiClient';

/**
 * Fetch all inspections for the current user
 * @param {Object} filters - Optional filters (status, vehicleId, limit)
 * @returns {Promise<Array>} Array of inspection records
 */
export async function fetchInspections(filters = {}) {
  try {
    // Build query params
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.vehicleId) params.append('vehicleId', filters.vehicleId);
    if (filters.limit) params.append('limit', filters.limit);

    const queryString = params.toString();
    const url = `/inspections${queryString ? `?${queryString}` : ''}`;

    console.log('Fetching inspections from:', url);

    const response = await apiClient.get(url);
    console.log('Inspections fetched:', response.data);

    return response.data.inspections || [];
  } catch (error) {
    console.error('Error fetching inspections:', error);
    throw error.response?.data?.error
      ? new Error(error.response.data.error)
      : error;
  }
}

/**
 * Fetch a specific inspection by ID
 * @param {string} inspectionId - The inspection ID
 * @returns {Promise<Object>} Inspection record with full details
 */
export async function fetchInspectionById(inspectionId) {
  try {
    const url = `/inspections/${inspectionId}`;

    console.log('Fetching inspection:', url);

    const response = await apiClient.get(url);
    console.log('Inspection fetched:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error fetching inspection:', error);
    if (error.response?.status === 404) {
      throw new Error('Inspection not found');
    }
    throw error.response?.data?.error
      ? new Error(error.response.data.error)
      : error;
  }
}

/**
 * Delete a specific inspection by ID
 * @param {string} inspectionId - The inspection ID to delete
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteInspection(inspectionId) {
  try {
    const url = `/inspections/${inspectionId}`;

    console.log('Deleting inspection:', url);

    const response = await apiClient.delete(url);
    console.log('Inspection deleted:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error deleting inspection:', error);
    if (error.response?.status === 404) {
      throw new Error('Inspection not found');
    }
    if (error.response?.status === 403) {
      throw new Error('Not authorized to delete this inspection');
    }
    throw error.response?.data?.error
      ? new Error(error.response.data.error)
      : error;
  }
}

/**
 * Delete a specific segment from an inspection
 * @param {string} inspectionId - The inspection ID
 * @param {string} filename - The segment filename (e.g., "2026_02_03_15_53_29_recording.webm")
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteSegment(inspectionId, filename) {
  try {
    const url = `/inspections/${inspectionId}/segments/${encodeURIComponent(filename)}`;

    console.log('Deleting segment:', url);

    const response = await apiClient.delete(url);
    console.log('Segment deleted:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error deleting segment:', error);
    if (error.response?.status === 404) {
      throw new Error('Segment not found');
    }
    if (error.response?.status === 403) {
      throw new Error('Not authorized to delete this segment');
    }
    throw error.response?.data?.error
      ? new Error(error.response.data.error)
      : error;
  }
}

/**
 * Fetch user's enrichment settings
 * @returns {Promise<Object>} User settings including enrichmentPrompt
 */
export async function fetchUserSettings() {
  try {
    const url = `/user/settings`;

    console.log('Fetching user settings from:', url);

    const response = await apiClient.get(url);
    console.log('User settings fetched:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error fetching user settings:', error);
    throw error.response?.data?.error
      ? new Error(error.response.data.error)
      : error;
  }
}

/**
 * Update user's enrichment settings
 * @param {Object} settings - Settings to update
 * @param {string} [settings.enrichmentPrompt] - The new enrichment prompt
 * @param {boolean} [settings.mediaButtonEnabled] - Enable headset media button
 * @param {string[]} [settings.inputDeviceHistory] - List of recently used input devices
 * @returns {Promise<Object>} Updated settings
 */
export async function updateUserSettings(settings) {
  try {
    const url = `/user/settings`;

    // Support legacy call with just a string (enrichmentPrompt)
    const payload = typeof settings === 'string'
      ? { enrichmentPrompt: settings }
      : settings;

    console.log('Updating user settings at:', url, payload);

    const response = await apiClient.put(url, payload);
    console.log('User settings updated:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error updating user settings:', error);
    throw error.response?.data?.error
      ? new Error(error.response.data.error)
      : error;
  }
}

/**
 * Re-enrich an inspection with current user's prompt
 * @param {string} inspectionId - The inspection ID to re-enrich
 * @returns {Promise<Object>} Updated inspection with new processed transcript
 */
export async function reEnrichInspection(inspectionId) {
  try {
    const url = `/inspections/${inspectionId}/re-enrich`;

    console.log('Re-enriching inspection:', url);

    const response = await apiClient.post(url);
    console.log('Inspection re-enriched:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error re-enriching inspection:', error);
    throw error.response?.data?.error
      ? new Error(error.response.data.error)
      : error;
  }
}

/**
 * Test enrichment with sample text using user's current prompt
 * @param {string} sampleText - The sample text to enrich
 * @returns {Promise<Object>} Original and enriched text
 */
export async function testEnrichment(sampleText) {
  try {
    const url = `/user/test-enrichment`;

    console.log('Testing enrichment at:', url);

    const response = await apiClient.post(url, { sampleText });
    console.log('Enrichment test result:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error testing enrichment:', error);
    throw error.response?.data?.error
      ? new Error(error.response.data.error)
      : error;
  }
}

/**
 * Delete user account and all associated data
 * @returns {Promise<Object>} Deletion result with counts
 */
export async function deleteAccount() {
  try {
    const url = `/user/account`;

    console.log('Deleting account at:', url);

    const response = await apiClient.delete(url);
    console.log('Account deleted:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error deleting account:', error);
    throw error.response?.data?.error
      ? new Error(error.response.data.error)
      : error;
  }
}

/**
 * Delete all inspections for the current user
 * @returns {Promise<Object>} Deletion result with counts
 */
export async function deleteAllInspections() {
  try {
    const url = `/user/inspections`;

    console.log('Deleting all inspections at:', url);

    const response = await apiClient.delete(url);
    console.log('All inspections deleted:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error deleting all inspections:', error);
    throw error.response?.data?.error
      ? new Error(error.response.data.error)
      : error;
  }
}

/**
 * Update user's data sharing consent
 * @param {boolean} consent - Whether user consents to data sharing
 * @returns {Promise<Object>} Updated settings
 */
export async function updateUserConsent(consent) {
  try {
    const url = `/user/settings`;

    console.log('Updating user consent at:', url);

    const response = await apiClient.put(url, { dataShareConsent: consent });
    console.log('User consent updated:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error updating user consent:', error);
    throw error.response?.data?.error
      ? new Error(error.response.data.error)
      : error;
  }
}

/**
 * Submit feedback (like/dislike) for a transcript
 * @param {string} inspectionId - The inspection ID
 * @param {string} transcriptType - 'raw' or 'processed'
 * @param {string} rating - 'like' or 'dislike'
 * @returns {Promise<Object>} Feedback submission result
 */
export async function submitTranscriptFeedback(inspectionId, transcriptType, rating) {
  try {
    const url = `/inspections/${inspectionId}/feedback`;

    console.log('Submitting transcript feedback:', url, { transcriptType, rating });

    const response = await apiClient.post(url, { transcriptType, rating });
    console.log('Transcript feedback submitted:', response.data);

    return response.data;
  } catch (error) {
    console.error('Error submitting transcript feedback:', error);
    throw error.response?.data?.error
      ? new Error(error.response.data.error)
      : error;
  }
}
